import { useId, useState, type CSSProperties, type ReactNode } from 'react';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/utils/cn';
import { Button } from '@/components/ui/Button';
import { UnsavedChangesModal } from '@/components/ui/UnsavedChangesModal';
import { useIsDesktopNav } from '@/hooks/useNavBreakpoint';
import { TYPE } from '@/lib/typography';

export function SlideOver({
  open,
  title,
  description,
  onClose,
  children,
  footer,
  wide = false,
  width,
  dirty = false,
  closeLabel,
}: {
  open: boolean;
  title: string;
  description?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
  /** Desktop max width in px. Ignored on mobile, where the panel is full screen. */
  width?: number;
  dirty?: boolean;
  closeLabel?: string;
}) {
  const { t } = useTranslation();
  const titleId = useId();
  const isDesktop = useIsDesktopNav();
  const [unsavedOpen, setUnsavedOpen] = useState(false);
  const panelStyle: CSSProperties | undefined =
    isDesktop && width != null ? { width, maxWidth: width } : undefined;

  const requestClose = () => {
    if (dirty) setUnsavedOpen(true);
    else onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={requestClose} className="relative z-40">
        <DialogBackdrop className="fixed inset-0 bg-ds-scrim" />
        <div className="fixed inset-0 flex justify-end">
          <DialogPanel
            aria-labelledby={titleId}
            style={panelStyle}
            className={cn(
              'flex h-full w-full flex-col bg-ds-card shadow-[var(--sh-so)]',
              !isDesktop
                ? 'max-w-none'
                : width != null
                  ? 'max-w-none'
                  : wide
                    ? 'max-w-slide-over-wide'
                    : 'max-w-slide-over',
            )}
          >
            <header className="flex shrink-0 items-start gap-s3 border-b border-ds-line px-s5 pb-s4 pt-5">
              <div className="min-w-0 flex-1">
                <DialogTitle id={titleId} className={cn(TYPE.heading, 'text-ds-text')}>
                  {title}
                </DialogTitle>
                {description ? <p className={cn(TYPE.small, 'mt-0.5 text-ds-muted')}>{description}</p> : null}
              </div>
              {closeLabel ? (
                <Button type="button" variant="secondary" onClick={requestClose} aria-label={closeLabel}>
                  <X />
                  {closeLabel}
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={requestClose}
                  aria-label={t('common.close')}
                >
                  <X />
                </Button>
              )}
            </header>
            <div className="min-h-0 flex-1 overflow-y-auto p-s5">{children}</div>
            {footer ? (
              <footer className="flex shrink-0 items-center gap-s2 border-t border-ds-line bg-ds-card px-s5 py-s4">
                {footer}
              </footer>
            ) : null}
          </DialogPanel>
        </div>
      </Dialog>
      <UnsavedChangesModal
        open={unsavedOpen}
        onContinue={() => setUnsavedOpen(false)}
        onDiscard={() => {
          setUnsavedOpen(false);
          onClose();
        }}
      />
    </>
  );
}

export function SlideOverFooter({
  onCancel,
  primary,
}: {
  onCancel: () => void;
  primary: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <>
      <Button type="button" variant="tertiary" onClick={onCancel}>
        {t('common.cancel')}
      </Button>
      <div className="ml-auto">{primary}</div>
    </>
  );
}
